import React, { useEffect, useState } from "react";
import Footer from "../Component/Footer";
import Header from "../Component/Header";
import { Users, Target, Heart, ArrowRight, Lightbulb, School, Home, Zap, Globe, Award, CheckCircle } from "lucide-react";

function WeDoIt() {
  const [isVisible, setIsVisible] = useState({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({
              ...prev,
              [entry.target.id]: true
            }));
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const approaches = [
    {
      icon: Home,
      title: "Community-Based Work",
      description: "Direct engagement within communities where we can access and impact the most target persons effectively",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: School,
      title: "Educational Partnerships",
      description: "Strategic collaborations with schools to reach youth and provide comprehensive programming",
      color: "from-green-500 to-emerald-500"
    },
    {
      icon: Users,
      title: "Group Mobilization",
      description: "Organizing and working with diverse groups to maximize outreach and community participation",
      color: "from-purple-500 to-pink-500"
    },
    {
      icon: Lightbulb,
      title: "Innovative Engagement",
      description: "Creative and innovative methods to capture attention and ensure meaningful participation",
      color: "from-orange-500 to-red-500"
    }
  ];

  const targetGroups = [
    { icon: Users, label: "Youth", description: "Young people as drivers of change" },
    { icon: Heart, label: "Women", description: "Empowering women for community development" },
    { icon: Globe, label: "Men", description: "Engaging male community leaders" },
    { icon: School, label: "Students", description: "Educational institution partnerships" }
  ];

  const methodology = [
    {
      step: "1",
      title: "Community Assessment",
      description: "Identify target communities and assess their specific needs and opportunities",
      icon: Target
    },
    {
      step: "2", 
      title: "Strategic Engagement",
      description: "Deploy innovative methods to capture attention and build meaningful connections",
      icon: Lightbulb
    },
    {
      step: "3",
      title: "Inclusive Programming",
      description: "Develop and implement programs that ensure active participation from all target groups",
      icon: Users
    },
    {
      step: "4",
      title: "Sustainable Impact",
      description: "Create lasting change through continuous engagement and community ownership",
      icon: Award
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50">
      <Header />
      
      {/* Support Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 py-4">
        <div className="text-center">
          <h3 className="text-white text-xl md:text-2xl font-semibold">
            Support Kayunga Community Club
          </h3>
        </div>
      </div>
      
      {/* Hero Image Section */}
      <div className="relative pt-2">
        <div className="w-full h-64 md:h-80 overflow-hidden">
          <img 
            src="img/main.JPG" 
            className="w-full h-full object-cover object-center"
            alt="How We Work"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
        
        {/* Hero Content Overlay */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white px-4">
            <div className="inline-flex items-center bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <Zap className="w-4 h-4 mr-2" />
              Our Methodology
            </div>
            <h1 className="text-4xl md:text-6xl font-black mb-4">
              <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                How We Do It!
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto">
              Innovative community engagement strategies for lasting impact
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="py-20">
        <div className="max-w-7xl mx-auto px-4">

          {/* Approach Methods */}
          <div 
            id="approaches-grid"
            data-animate
            className={`mb-16 transform transition-all duration-1000 delay-300 ${
              isVisible['approaches-grid'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <div className="text-center mb-12">
              <h3 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Our <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Methods</span>
              </h3>
              <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full mx-auto"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {approaches.map((approach, index) => {
                const Icon = approach.icon;
                return (
                  <div
                    key={index}
                    className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
                  >
                    <div className={`w-16 h-16 bg-gradient-to-r ${approach.color} rounded-2xl flex items-center justify-center mb-6 mx-auto`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h4 className="text-xl font-bold text-gray-800 mb-4 text-center">{approach.title}</h4>
                    <p className="text-gray-600 text-center leading-relaxed">{approach.description}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Target Groups */}
          <div 
            id="target-groups"
            data-animate
            className={`mb-16 transform transition-all duration-1000 delay-500 ${
              isVisible['target-groups'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-1 shadow-2xl">
              <div className="bg-white rounded-3xl p-8 md:p-12">
                <div className="text-center mb-12">
                  <h3 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                    Who We <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Engage</span>
                  </h3>
                  <p className="text-xl text-gray-600">Our comprehensive approach includes all community members</p>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                  {targetGroups.map((group, index) => {
                    const Icon = group.icon;
                    return (
                      <div key={index} className="text-center">
                        <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center mb-4 mx-auto">
                          <Icon className="w-10 h-10 text-white" />
                        </div>
                        <h4 className="text-lg font-bold text-gray-800 mb-2">{group.label}</h4>
                        <p className="text-sm text-gray-600">{group.description}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Methodology Steps */}
          <div 
            id="methodology"
            data-animate
            className={`transform transition-all duration-1000 delay-700 ${
              isVisible['methodology'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <div className="text-center mb-12">
              <h3 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Our <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Process</span>
              </h3>
              <p className="text-xl text-gray-600">Step-by-step methodology for effective community engagement</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {methodology.map((step, index) => {
                const Icon = step.icon;
                return (
                  <div key={index} className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                    <div className="flex items-start space-x-4">
                      <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-xl">{step.step}</span>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center mb-3">
                          <Icon className="w-6 h-6 text-blue-600 mr-2" />
                          <h4 className="text-xl font-bold text-gray-800">{step.title}</h4>
                        </div>
                        <p className="text-gray-600 leading-relaxed">{step.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Call to Action Section */}
      <div className="py-20 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 text-center">
          <div 
            id="cta-content"
            data-animate
            className={`transform transition-all duration-1000 ${
              isVisible['cta-content'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <div className="inline-flex items-center bg-white/20 backdrop-blur-sm text-white px-6 py-3 rounded-full text-lg font-semibold mb-8">
              <CheckCircle className="w-5 h-5 mr-3" />
              Engage • Include • Impact
            </div>
            
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-8">
              Partner With Our Method
            </h2>
            <p className="text-xl text-white/90 mb-12 max-w-3xl mx-auto">
              Join us in implementing proven community engagement strategies that create 
              meaningful inclusion and lasting impact across Kayunga communities.
            </p>
            
            <button 
              onClick={() => window.location.href = '/contact-us'}
              className="bg-white text-blue-600 px-10 py-4 rounded-2xl font-bold text-lg transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center gap-2 mx-auto"
            >
              Work With Us <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default WeDoIt;